import { z } from 'zod';
import { FEEDBACK_TAGS } from '../domain/feedback-tags-vocabulary';

/**
 * Block 10 «Что улучшить?» — Zod-контракты для `POST /api/feedback`.
 *
 * Расширяет существующий `FeedbackCreateSchema` из `docs/architecture/api-contracts.md` §10
 * (фиксируется ADR-0018 — P-011 require ADR for contract changes).
 *
 * Варианты union: kind='screen_improvement' (Block 10, ADR-0018) и
 * kind='partner_review' (Block 8 «Партнёры», ADR-0082 — отзыв о подрядчике 2.8.4).
 * Вариант `general` (Block 11) — TODO-комментарий ниже, без stub-схемы-блокера
 * (это запутает будущего разработчика — он импортирует заглушку, не понимая что
 * это валидатор-блокер; см. ADR-0018 §Контекст п.4).
 *
 * ── Архитектурная заметка про superRefine + discriminatedUnion ───────────
 * Zod v3 `z.discriminatedUnion()` принимает только чистые `ZodObject` (без
 * `.superRefine`/`.refine`/`.transform`). Поэтому `customTag` refinement
 * вынесен в named function `customTagRefinement` и применяется в двух местах:
 *   1. `FeedbackScreenImprovementSchema = base.superRefine(customTagRefinement)`
 *      — standalone parse (UI form layer).
 *   2. `FeedbackCreateSchema = discriminatedUnion([base]).superRefine(...)`
 *      — server-side request body parse; dispatch на screen_improvement.
 * Оба пути дают идентичную семантику валидации (ADR-0018 §Q1).
 *
 * References:
 *   - ADR-0018 §Q1 (final schema + superRefine logic)
 *   - PRD `docs/prd/v4/14-block-10-feedback.md` (≥50 символов, до 3 фото, 6 тегов)
 *   - `data-model.md` §12.5 (БД-таблица feedback)
 *   - Vocabulary: `domain/feedback-tags-vocabulary.ts`
 */

export const FeedbackTagSchema = z.enum(FEEDBACK_TAGS);

const FeedbackScreenImprovementBaseSchema = z.object({
  kind: z.literal('screen_improvement'),
  bodyMd: z.string().min(50).max(5000),
  tags: z.array(FeedbackTagSchema).max(6).default([]),
  customTag: z.string().min(1).max(100).optional(),
  blockId: z.string().min(1).max(50),
  screenId: z.string().min(1).max(50),
  screenshotR2Key: z.string().min(1).max(500).nullable(),
  attachedPhotosR2Keys: z.array(z.string().min(1).max(500)).max(3).default([]),
});

type FeedbackScreenImprovementData = z.infer<typeof FeedbackScreenImprovementBaseSchema>;

const customTagRefinement = (data: FeedbackScreenImprovementData, ctx: z.RefinementCtx): void => {
  if (data.tags.includes('other') && !data.customTag) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'customTag обязателен при выборе тега "other"',
      path: ['customTag'],
    });
  }
  if (!data.tags.includes('other') && data.customTag) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'customTag допустим только при теге "other"',
      path: ['customTag'],
    });
  }
};

export const FeedbackScreenImprovementSchema =
  FeedbackScreenImprovementBaseSchema.superRefine(customTagRefinement);

/**
 * Block 8 «Партнёры», экран 2.8.4 «Что улучшить подрядчику» (ADR-0082).
 *
 * Все поля обязательны (решение Aibek 2026-06-14): подрядчик + текст + номер
 * заказа + фото. **БЕЗ `rating`** — звёзды убраны (привести к прототипу).
 * Cross-field refinement не нужен (нет условных полей вроде `customTag`), поэтому
 * это чистый `ZodObject` — годен и для standalone-parse, и для `discriminatedUnion`.
 *
 * `photoR2Key` — обязательный ключ фото в приватном R2 (signed URL на отдаче,
 * ADR-0046); verbatim, без regex/transform/normalize (P-001) — связь с именем
 * объекта в R2 не ломаем. `.max(500)` — паритет со `screenshotR2Key`.
 *
 * Согласие на публикацию — действием (подтекст на форме E3); фиксируется в записи
 * (E2). Хранение записи + `userId` → региональный Postgres (P-007/P-009).
 */
export const FeedbackPartnerReviewSchema = z.object({
  kind: z.literal('partner_review'),
  partnerId: z.string().min(1),
  bodyMd: z.string().min(1).max(2000),
  orderNumber: z.string().min(1).max(100),
  photoR2Key: z.string().min(1).max(500),
});

// TODO B-084 (Block 11): FeedbackGeneralSchema — schema для general kind.
// Будет добавлено при реализации Block 11 «Личный кабинет».

export const FeedbackCreateSchema = z
  .discriminatedUnion('kind', [FeedbackScreenImprovementBaseSchema, FeedbackPartnerReviewSchema])
  .superRefine((data, ctx) => {
    if (data.kind === 'screen_improvement') {
      customTagRefinement(data, ctx);
    }
    // kind='partner_review' — cross-field refinement не требуется (все поля
    // безусловно обязательны, проверяются базовой схемой).
  });

export const FeedbackCreateResponseSchema = z.object({
  id: z.string().uuid(),
  status: z.literal('new'),
});

export type FeedbackScreenImprovement = z.infer<typeof FeedbackScreenImprovementSchema>;
export type FeedbackPartnerReview = z.infer<typeof FeedbackPartnerReviewSchema>;
export type FeedbackCreate = z.infer<typeof FeedbackCreateSchema>;
export type FeedbackCreateResponse = z.infer<typeof FeedbackCreateResponseSchema>;
